import React, { FC, useEffect, useState } from 'react';
import { Badge } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
import './Header.scss';
import { useTranslation } from 'react-i18next';
import { useCookies } from 'react-cookie';
import { clarity } from 'react-microsoft-clarity';
import Joyride, { ACTIONS, CallBackProps, EVENTS, STATUS, Step } from 'react-joyride';
import { tourSteps, tourSkipped, tourStyles } from './ProductTourInfo';
import StartLearnLightUA from '../../../assets/images/startLearn/LightUA.png';
import StartLearnLightEN from '../../../assets/images/startLearn/LightEN.png';
import StartLearnDarkUA from '../../../assets/images/startLearn/DarkUA.png';
import StartLearnDarkEN from '../../../assets/images/startLearn/DarkEN.png';
import { Logo } from './Logo';
import { NavBar } from './NavBar';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { langSlice } from '../../../store/reducers/LangSlice';
import { authSlice } from '../../../store/reducers/AuthSlice';
import { MobileSidebar } from './MobileSidebar';
import { useWindowDimension } from '../../../hooks/useWindowDimension';
import { DropdownSelect } from '../DropdownSelect';
import { ApiError } from '../../../models/ApiError';
import { formatLocale, headerNewsLink, headerNewsText, getErrorMessage } from '../../../utils';
import { PushNotifications } from '../../pushNotifications';
import { userInfoApi } from '../../../services/apiServices/user/userInfo';
import { authorizationApi } from '../../../services/apiServices/user/authorization';
import { openNewBrowserTab } from '../../../services/domService';
import { BasicButton } from '../buttons/BasicButton/BasicButton';
import { ReactComponent as CallMeIcon } from '../../../assets/images/CallMe.svg';
import { ReactComponent as BurgerCloseIcon } from '../../../assets/images/burger/BurgerClose.svg';
import { ReactComponent as NotificationIcon } from '../../../assets/images/NotificationsIcon.svg';
import { ReactComponent as SevenDaysIcon } from '../../../assets/images/SevenDays.svg';
import { useCustomSnackbar } from '../../../hooks/useCustomEnqueueSnackbar';
import { appSlice } from '../../../store/reducers/AppSlice';
import { userInfoSlice, UserInfoState } from '../../../store/reducers/UserInfoSlice';
import { modalSlice } from '../../../store/reducers/ModalSlice';
import { useDebounce } from '../../../hooks/useDebounce';
import { useCallMeBack } from '../../../hooks/useCallMeBack';
import { ReactComponent as Menu } from '../../../assets/images/burger/NewBurgerOpen.svg';
import {
  BannerApp,
  BannerAppState,
  CookieItems,
  ModalVariants,
  ScreenSize,
  GlobalRouteNames,
  SessionStorageItems,
  LocalStorageItems,
} from '../../../models/Common';
import { NotificationsApi } from '../../../models/PushNotification';
import { ReactComponent as Arrow } from '../../../assets/images/ArrowHeaderNews.svg';
import { clarityId, headerNewsAvailable, isProductTourEnabled } from '../../../constants/configuration';
import { UserInfoBlock } from '../../homeParts/UserInfoBlock';
import { BannerDownloadApp } from './BannerDownloadApp';
import { UseGetAuthLinkBrizTv } from '../../../hooks/UseGetAuthLinkBrizTv';

export const Header: FC<NotificationsApi> = ({
  getMessages,
  prevPageUrl,
  nextPageUrl,
  pushNotifications,
  isNotificationsLoading,
}) => {
  const { t, i18n } = useTranslation();
  const { search, pathname } = useLocation();
  const { getAuthLinkBrizTv, isLoadingGetAuthLinkBrizTv } = UseGetAuthLinkBrizTv();
  const history = useHistory();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [steps, setSteps] = useState<Step[]>(tourSteps(t));
  const [isOpenSidebar, setIsOpenSidebar] = useState(false);
  const [isNewNotifications, setIsNewNotifications] = useState(false);
  const { width } = useWindowDimension();
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const { selectedLanguage, availableLanguages } = useAppSelector((state) => state.langReducer);
  const { isAuth } = useAppSelector((state) => state.authReducer);
  const { theme, isSevenDaysAvailable } = useAppSelector((state) => state.appReducer);
  const { isUserInfoDownloaded } = useAppSelector((state) => state.userInfoReducer);
  const dispatch = useAppDispatch();
  const { openPushNotification } = appSlice.actions;
  const { selectLanguage } = langSlice.actions;
  const { logout } = authSlice.actions;
  const { openModal } = modalSlice.actions;
  const [logoutApi] = authorizationApi.useLogoutMutation();
  const [changeUserServiceLanguageApi] = userInfoApi.useChangeUserServiceLanguageMutation();
  const { setUserInfo, resetUserInfoAfterLogout } = userInfoSlice.actions;
  const isBannerClosed = sessionStorage.getItem(SessionStorageItems.BannerApp) as BannerAppState;
  const [isShownBanner, setIsShownBanner] = useState<BannerAppState>(isBannerClosed || BannerApp.Opened);

  const {
    data: userInfo,
    isLoading: userInfoIsLoading,
    isSuccess,
  } = userInfoApi.useGetUserInfoQuery('', {
    skip: !isAuth,
  });
  const [cookie] = useCookies([`${CookieItems.PRODUCT_TOUR}_${userInfo?.id}`]);

  const { callMeBack } = useCallMeBack();

  useEffect(() => {
    if (width >= 1280) {
      setIsOpenSidebar(false);
    }
  }, [width]);

  const setStep = useDebounce((index: number) => {
    setStepIndex(index);
  }, 200);

  const getSkipStepImage = () => {
    let imgSrc = StartLearnLightUA;
    if (theme === 'light' && selectedLanguage === 'en') {
      imgSrc = StartLearnLightEN;
    } else if (theme === 'dark' && selectedLanguage !== 'en') {
      imgSrc = StartLearnDarkUA;
    } else if (theme === 'dark' && selectedLanguage === 'en') {
      imgSrc = StartLearnDarkEN;
    }
    return imgSrc;
  };

  const handleTourStart = (skip?: unknown) => {
    if (skip) {
      setSteps(tourSkipped(t, getSkipStepImage()));
    }
    setRun(true);
  };

  useEffect(() => {
    if (run && isOpenSidebar) {
      setStep(steps.length - 1);
    }
  }, [isOpenSidebar]);

  useEffect(() => {
    if (
      isProductTourEnabled &&
      isUserInfoDownloaded &&
      (!cookie[`${CookieItems.PRODUCT_TOUR}_${userInfo?.id}`] ||
        +cookie[`${CookieItems.PRODUCT_TOUR}_${userInfo?.id}`] !== userInfo?.id)
    ) {
      dispatch(
        openModal({
          modalVariants: ModalVariants.ProductTourStart,
          content: {
            handleProceed: handleTourStart,
            userId: userInfo?.id,
          },
        }),
      );
    }
  }, [isUserInfoDownloaded]);

  useEffect(() => {
    if (!userInfoIsLoading && isSuccess) {
      const userInfoResult: UserInfoState = {
        userInfoState: userInfo,
        isUserInfoDownloaded: true,
      };
      dispatch(setUserInfo(userInfoResult));
    }
  }, [userInfo]);

  const changeUserServiceLanguage = async (lang: string) => {
    try {
      await changeUserServiceLanguageApi({ lang }).unwrap();
      setSteps(tourSteps(t));
    } catch (err) {
      const message = getErrorMessage(err);
      customEnqueueSnackbar(message, 'error');
    }
  };

  const handleLanguage = (selected: string) => {
    i18n.changeLanguage(formatLocale(selected)).then(() => {
      dispatch(selectLanguage(selected));
    });
    if (isAuth) changeUserServiceLanguage(selected);

    const queryParams = new URLSearchParams(search);
    if (queryParams.has('lang')) {
      queryParams.delete('lang');
      history.replace({
        search: queryParams.toString(),
      });
    }
  };

  const handleLogout = async () => {
    try {
      await logoutApi('')
        .unwrap()
        .then(() => {
          localStorage.removeItem(LocalStorageItems.ControlledAccount);
          dispatch(resetUserInfoAfterLogout());
          dispatch(logout());
        });
    } catch (err) {
      const error = err as ApiError;
      if (error.status !== 403) {
        const message = getErrorMessage(err);
        customEnqueueSnackbar(message, 'error');
      }
    }
  };

  const handleBurger = () => {
    setIsOpenSidebar(!isOpenSidebar);
    dispatch(openPushNotification(false));
  };

  const handleCloseDrawers = () => {
    setIsOpenSidebar(false);
    dispatch(openPushNotification(false));
  };

  const handleCloseMessages = () => {
    setIsOpenSidebar(false);
    getMessages('');
    dispatch(openPushNotification(false));
  };

  const handleSevenDays = () => {
    openNewBrowserTab(process.env.REACT_APP_LOOP_PAGE || '');
  };

  const handleNotification = () => {
    setIsOpenSidebar(false);
    getMessages('');
    dispatch(openPushNotification(true));
  };

  const handleBrizTv = async () => {
    if (isLoadingGetAuthLinkBrizTv) return;
    const link = await getAuthLinkBrizTv();
    openNewBrowserTab(link);
  };

  const closeBannerApp = () => {
    setIsShownBanner(BannerApp.Closed);
    sessionStorage.setItem(SessionStorageItems.BannerApp, BannerApp.Closed);
  };

  useEffect(() => {
    if (run && pathname !== GlobalRouteNames.HomePage) {
      handleCloseDrawers();
      if (steps.length > 1) {
        setSteps(tourSkipped(t, getSkipStepImage()));
      } else {
        setRun(false);
        setSteps(tourSteps(t));
      }
      setStepIndex(0);
    }
  }, [pathname]);

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { action, index, status, type, step } = data;
    if (action === ACTIONS.CLOSE) {
      handleCloseDrawers();
      if (steps.length > 1) {
        setSteps(tourSkipped(t, getSkipStepImage()));
      } else {
        setRun(false);
        setSteps(tourSteps(t));
      }
      setStepIndex(0);
      return;
    }
    if (type === EVENTS.STEP_AFTER) {
      const tutorial = step.title === t('productTour.tutorialTitle') && isOpenSidebar;
      const service = step.title === t('productTour.serviceCardTitle') && action === ACTIONS.NEXT && !isOpenSidebar;
      if (tutorial || service) handleBurger();
      if (service) return;
      setStep(index + (action === ACTIONS.PREV ? -1 : 1));
    } else if (status === STATUS.FINISHED) {
      setRun(false);
      if (steps.length > 1) {
        dispatch(
          openModal({
            modalVariants: ModalVariants.ProductTourEnd,
            content: {
              handleProceed: handleTourStart,
            },
          }),
        );
      } else {
        setSteps(tourSteps(t));
      }
      setStepIndex(0);
    }
  };

  /*  Microsoft clarity block start
    проверка на наличие clarity ID, чтобы с localhost или DEV не слать лишние запросы
  */
  useEffect(() => {
    if (clarityId && !clarity.hasStarted()) {
      clarity.init(clarityId);
    }
    if (userInfo?.id && clarity.hasStarted()) clarity.identify('USER_ID', { userProperty: userInfo?.id });
  }, [clarityId, userInfo?.id]);

  /* Microsoft clarity block end  */

  return (
    <header className="page-header" style={run && stepIndex === 1 ? { position: 'static' } : {}}>
      {!isAuth && isShownBanner === 'opened' ? <BannerDownloadApp hideBanner={closeBannerApp} /> : null}
      {headerNewsAvailable ? (
        <div className="page-header__news">
          <a
            href={headerNewsLink(selectedLanguage)}
            className="page-header__news__extra-link"
            target="_blank"
            rel="noreferrer"
          >
            <span className="page-header__news__extra-flash" />
            <span className="page-header__news__extra-flash" />
            <span className="page-header__news__extra-flash" />
            <span className="page-header__news__extra-text">
              <p>{headerNewsText(selectedLanguage)}</p>
              <Arrow />
            </span>
            <span className="page-header__news__extra-flash" />
            <span className="page-header__news__extra-flash" />
            <span className="page-header__news__extra-flash" />
          </a>
        </div>
      ) : null}
      <div className="page-header__container container">
        <Logo handleClose={handleCloseDrawers} />
        <div className="page-header__container">
          <DropdownSelect
            handleLanguage={handleLanguage}
            selectedLanguage={selectedLanguage}
            availableLanguages={availableLanguages}
          />
          <NavBar
            isAuth={isAuth}
            handleNotification={handleNotification}
            isNewNotification={isNewNotifications}
            handleBrizTv={handleBrizTv}
            handleCallback={callMeBack}
            width={width}
          />
          <div className="page-header__container_mobile">
            {isSevenDaysAvailable && (
              <BasicButton handleClick={handleSevenDays} className="button button__seven-days">
                <SevenDaysIcon />
              </BasicButton>
            )}
            {isAuth ? (
              <BasicButton handleClick={handleNotification} className="button button__notification">
                <Badge className="custom-badge icon" color="error" variant="dot" invisible={!isNewNotifications}>
                  <NotificationIcon />
                </Badge>
              </BasicButton>
            ) : null}
            <BasicButton handleClick={callMeBack} className="Button button__support-mobile">
              <CallMeIcon />
            </BasicButton>
            {isAuth && (
              <PushNotifications
                pushNotifications={pushNotifications}
                isNotificationsLoading={isNotificationsLoading}
                prevPageUrl={prevPageUrl}
                nextPageUrl={nextPageUrl}
                getMessages={getMessages}
                handleClose={handleCloseMessages}
                setIsNewMessages={setIsNewNotifications}
              />
            )}
          </div>
          {width <= ScreenSize.Desktop || isAuth ? (
            <BasicButton handleClick={handleBurger} className="button button__burger">
              {isOpenSidebar ? <BurgerCloseIcon /> : <Menu />}
            </BasicButton>
          ) : null}
          <MobileSidebar
            isOpen={isOpenSidebar}
            handleLanguage={handleLanguage}
            selectedLanguage={selectedLanguage}
            availableLanguages={availableLanguages}
            isAuth={isAuth}
            handleLogout={handleLogout}
            handleStartTour={handleTourStart}
            handleCloseSideBar={handleCloseDrawers}
            handleBrizTv={handleBrizTv}
            isShownBanner={isShownBanner}
            width={width}
          />
        </div>
      </div>
      <Joyride
        key={steps.length}
        continuous
        callback={handleJoyrideCallback}
        run={run}
        stepIndex={stepIndex}
        disableScrollParentFix
        steps={steps}
        scrollOffset={90}
        styles={tourStyles}
        locale={{
          close: t('productTour.close'),
          back: t('productTour.back'),
          next: t('productTour.next'),
          last: steps.length > 1 ? t('productTour.next') : t('productTour.last'),
        }}
      />
      {width < ScreenSize.Desktop && isAuth && pathname === GlobalRouteNames.HomePage && !isOpenSidebar ? (
        <>
          {isUserInfoDownloaded && <div className="user-info-block__divider" />}
          <div className="user-info-block__container container">
            <UserInfoBlock width={width} />
          </div>
          {isUserInfoDownloaded && <div className="user-info-block__gradient" />}
        </>
      ) : null}
    </header>
  );
};
